import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Clear,
  EditOutlined,
  Search,
  SortByAlpha,
  AdminPanelSettingsOutlined,
  VerifiedUserOutlined,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import MDSnackbar from "shared/components/MDSnackbar";
import DataTable from "shared/components/Tables/DataTable";
import { useMaterialUIController } from "app/providers";
import { getAllRolesService, updateRolService } from "modules/rol/services/rolService";
import { getAvailableViews } from "shared/utils/getAvailableViews";
import RoleDialog from "../Form/RoleDialog";
import {
  branchNodes,
  buildPermissionTree,
  isEnabled,
  permissionCounts,
} from "../Permissions/permissionUtils";

const ENTRIES = { defaultValue: 10, entries: [5, 10, 25, 50] };
function TablaRol({ refreshFlag }) {
  const [{ darkMode }] = useMaterialUIController();
  const foreground = darkMode ? "#edf2f7" : "#24364b";
  const muted = darkMode ? "#acbace" : "#64748b";
  const tint = darkMode ? "#233b55" : "#edf4fc";
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [alert, setAlert] = useState({ open: false, color: "info", message: "" });
  const tree = useMemo(() => buildPermissionTree(getAvailableViews()), []);
  const mounted = useRef(false);
  const request = useRef(0);
  const fetchRoles = useCallback(async () => {
    const current = ++request.current;
    setLoading(true);
    try {
      const response = await getAllRolesService();
      const data = typeof response === "string" ? JSON.parse(response) : response;
      if (!Array.isArray(data)) throw new Error("La respuesta de roles no es válida");
      if (mounted.current && current === request.current) setRoles(data);
    } catch (error) {
      if (mounted.current && current === request.current)
        setAlert({
          open: true,
          color: "error",
          message: error.message || "Error al obtener roles",
        });
    } finally {
      if (mounted.current && current === request.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    fetchRoles();
    return () => {
      mounted.current = false;
      request.current += 1;
    };
  }, [refreshFlag, fetchRoles]);

  const filtered = useMemo(
    () =>
      roles
        .filter((role) =>
          `${role.name || ""} ${role.id ?? ""}`.toLowerCase().includes(search.trim().toLowerCase())
        )
        .sort(
          (a, b) => (a.name || "").localeCompare(b.name || "") * (sortOrder === "asc" ? 1 : -1)
        ),
    [roles, search, sortOrder]
  );
  const updateRole = async (values) => {
    await updateRolService(selectedRole.id, values);
    setSelectedRole(null);
    setAlert({ open: true, color: "success", message: "Rol actualizado correctamente" });
    fetchRoles();
  };
  const rows = filtered.map((role) => {
    const permissions = role.more && typeof role.more === "object" ? role.more : {};
    const modules = tree.roots.filter((node) =>
      branchNodes(node).some((item) => isEnabled(permissions[item.key]))
    );
    const count = tree.roots.reduce(
      (total, node) => total + permissionCounts(node, permissions).selected,
      0
    );
    const avatarColor = ["#1a73e8", "#ef5350", "#43a047", "#fb8c00", "#e91e63", "#00897b"][
      Math.abs(Number(role.id) || 0) % 6
    ];
    return {
      id: (
        <Chip
          label={`#${role.id}`}
          size="small"
          sx={{
            height: 23,
            bgcolor: darkMode ? "#344256" : "#eef1f5",
            color: foreground,
            fontWeight: 700,
            fontSize: "0.7rem",
          }}
        />
      ),
      name: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: avatarColor,
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
              borderRadius: "50%",
              boxShadow: "0 2px 5px rgba(0,0,0,0.12)",
            }}
          >
            {(role.name || "?")
              .trim()
              .split(/\s+/)
              .map((word) => word[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                color: foreground,
                fontSize: "0.8125rem",
                textTransform: "uppercase",
                lineHeight: 1.5,
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              }}
            >
              {role.name || "Sin nombre"}
            </Typography>
          </Box>
        </Box>
      ),
      modules: (
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", maxWidth: 420 }}>
          {modules.slice(0, 3).map((node) => (
            <Chip
              key={node.key}
              size="small"
              label={node.name}
              sx={{
                height: 24,
                borderRadius: 1.5,
                bgcolor: tint,
                color: darkMode ? "#bedcff" : "#345b83",
                fontWeight: 500,
                fontSize: "0.72rem",
                "& .MuiChip-label": { px: 1.25 },
              }}
            />
          ))}
          {modules.length > 3 && (
            <Tooltip
              title={modules
                .slice(3)
                .map((node) => node.name)
                .join(", ")}
            >
              <Chip
                size="small"
                label={`+${modules.length - 3}`}
                sx={{
                  height: 24,
                  borderRadius: 1.5,
                  bgcolor: darkMode ? "#344256" : "#eef1f5",
                  color: foreground,
                  fontWeight: 600,
                }}
              />
            </Tooltip>
          )}
          {!modules.length && (
            <Typography variant="caption" color="text.secondary">
              Sin módulos habilitados
            </Typography>
          )}
        </Box>
      ),
      permissions: (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.65,
            borderRadius: 1.5,
            bgcolor: count ? (darkMode ? "#193e37" : "#ecf7f2") : darkMode ? "#2e394b" : "#f1f4f7",
            color: count ? (darkMode ? "#9de0c1" : "#28644c") : muted,
          }}
        >
          <VerifiedUserOutlined sx={{ fontSize: 16 }} />
          <Typography
            variant="caption"
            sx={{ color: "inherit", fontSize: "0.75rem", fontWeight: 600 }}
          >
            {count} {count === 1 ? "permiso" : "permisos"}
          </Typography>
        </Box>
      ),
      actions: (
        <Tooltip title="Editar permisos" arrow>
          <IconButton
            size="small"
            onClick={() => setSelectedRole(role)}
            disabled={loading}
            aria-label={`Editar permisos de ${role.name || "rol"}`}
            sx={{
              width: 36,
              height: 36,
              bgcolor: tint,
              color: darkMode ? "#b3d5ff" : "#1a73e8",
              "&:hover": { bgcolor: darkMode ? "#36516d" : "#dcecff" },
            }}
          >
            <EditOutlined sx={{ fontSize: 19 }} />
          </IconButton>
        </Tooltip>
      ),
    };
  });
  return (
    <>
      <Card
        sx={{
          overflow: "hidden",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 4px 18px rgba(20, 40, 65, 0.05)",
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2.5,
            background: "linear-gradient(100deg, #1a73e8, #49a3f1)",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flex: "1 1 auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 42,
                height: 42,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.15)",
                color: "#fff",
              }}
            >
              <AdminPanelSettingsOutlined />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ color: "#fff", fontWeight: 700 }}>
                Gestión de Roles
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
                Total de roles: {roles.length}
                {search ? ` · ${filtered.length} resultados` : ""}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: "right", color: "#fff" }}>
            <Typography variant="h5" sx={{ color: "inherit", fontWeight: 700 }}>
              {roles.length}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
              Roles registrados
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 1.75,
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <TextField
            size="small"
            placeholder="Buscar rol por nombre o ID..."
            inputProps={{ "aria-label": "Buscar roles" }}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                height: 42,
                borderRadius: 2,
                bgcolor: darkMode ? "#202c40" : "#f8fafc",
              },
              "& input": { fontSize: "0.8125rem", color: foreground },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Limpiar búsqueda de roles"
                    onClick={() => setSearch("")}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Tooltip title={sortOrder === "asc" ? "Ordenar de Z a A" : "Ordenar de A a Z"}>
            <IconButton
              sx={{ width: 38, height: 38, bgcolor: tint, color: darkMode ? "#b3d5ff" : "#1a73e8" }}
              aria-label="Cambiar orden de roles"
              onClick={() => setSortOrder((previous) => (previous === "asc" ? "desc" : "asc"))}
            >
              <SortByAlpha sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ height: 3 }}>{loading && <LinearProgress />}</Box>
        {loading && !roles.length ? (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <CircularProgress aria-label="Cargando roles" />
          </Box>
        ) : !filtered.length ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <Typography variant="body2">
              {search ? "No se encontraron roles para esta búsqueda." : "No hay roles disponibles."}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              overflowX: "auto",
              "& table": { minWidth: 1020, tableLayout: "fixed" },
              "& thead": { display: "table-header-group", p: 0 },
              "& thead th": {
                bgcolor: darkMode ? "#263449" : "#f5f7fa",
                borderBottom: "1px solid",
                borderColor: "divider",
                py: 1.75,
              },
              "& thead th > div": {
                color: muted,
                opacity: 1,
                fontSize: "0.68rem",
                letterSpacing: "0.04em",
              },
              "& tbody td": { py: 1.25, borderColor: "divider" },
              "& tbody td > div": { width: "100%" },
              "& tbody tr": { transition: "background-color 150ms ease" },
              "& tbody tr:hover": { bgcolor: darkMode ? "#27384b" : "#f8fbff" },
            }}
          >
            <DataTable
              table={{
                columns: [
                  { Header: "ID", accessor: "id", width: "8%", align: "center" },
                  { Header: "Rol", accessor: "name", width: "29%" },
                  { Header: "Módulos habilitados", accessor: "modules", width: "31%" },
                  { Header: "Permisos habilitados", accessor: "permissions", width: "18%" },
                  { Header: "Acciones", accessor: "actions", width: "14%", align: "center" },
                ],
                rows,
              }}
              isSorted={false}
              entriesPerPage={ENTRIES}
              autoResetPage={false}
              paginationResetKey={`${search}:${sortOrder}`}
              showTotalEntries
              noEndBorder
            />
          </Box>
        )}
      </Card>
      <RoleDialog
        open={Boolean(selectedRole)}
        initialData={selectedRole}
        onClose={() => setSelectedRole(null)}
        onSubmit={updateRole}
      />
      <MDSnackbar
        color={alert.color}
        icon={alert.color === "success" ? "check" : "warning"}
        title="Roles y permisos"
        content={alert.message}
        dateTime="Ahora"
        open={alert.open}
        onClose={() => setAlert((previous) => ({ ...previous, open: false }))}
        close={() => setAlert((previous) => ({ ...previous, open: false }))}
        bgWhite
      />
    </>
  );
}
TablaRol.propTypes = { refreshFlag: PropTypes.bool };
export default TablaRol;
